import { combineReducers } from 'redux';
import users, * as fromUsers from './users/reducers';
import schools, * as fromSchools from './schools/reducers';
import classes, * as fromClasses from './classes/reducers';
import forms, * as fromForms from './forms/reducers';
import fields, * as fromFields from './fields/reducers';
import ui, * as fromUi from './ui/reducers';
import stats, * as fromStats from './stats/reducers';
import lists, * as fromLists from './lists/reducers';
import locales, * as fromLocales from './locales/reducers';

const rootReducer = combineReducers({
  users,
  classes,
  forms,
  fields,
  schools,
  ui,
  stats,
  lists,
  locales,
});

export default rootReducer;

const getUsersInAList = (state, reducer) => {
  const list = state.lists.byId.items[reducer];
  if (!list || !list.ids) return null;
  const userList = [];
  list.ids.map(id => userList.push(state.users.byId.items[id]));
  return userList;
};

export const getUser = (state, id) => fromUsers.getUser(state.users, id);
export const getUserError = state => fromUsers.getUserError(state.users);
export const getActiveUser = state => fromUsers.getActiveUser(state.users);
export const getActiveSchoolId = state =>
  fromUsers.getActiveSchoolId(state.users);
export const getIsFetchingActiveUser = state =>
  state.users.activeUser.isFetching;
export const getIsFetchingUsers = state => state.users.byId.isFetching;
export const getStudentsInClass = (state, classId) =>
  getUsersInAList(state, `students_in_class_${classId}`);
export const getTeachersForActiveSchool = state =>
  getUsersInAList(state, 'teachers_in_school');
export const getStudentsForActiveSchool = state =>
  getUsersInAList(state, 'students_in_school');
export const getIsPostingLogin = state => state.users.auth.isPosting;
export const getHasUIHintBeenSeen = (state, hint) =>
  fromUsers.getHasUIHintBeenSeen(state.users, hint);
export const getAuthMessage = state => fromUsers.getAuthMessage(state.users);
export const getFailedLoginErrorVars = state =>
  state.users.auth.failedLoginErrorVars;
export const getAuthToken = state => state.users.auth.token;
export const getValidatedSchoolCode = state =>
  fromUsers.getValidatedSchoolCode(state.users);
export const isAuthTokenExpired = state =>
  fromUsers.isAuthTokenExpired(state.users);
export const isSensitiveTokenExpired = state =>
  fromUsers.isSensitiveTokenExpired(state.users);
export const isInviteTokenValid = state => state.users.invites.isValid;
export const isInviteTokenBeingValidated = state =>
  state.users.invites.isValidatingToken;
export const getInvitedUser = state => state.users.invites.user;
export const getInviteTokenMessage = state => state.users.invites.message;

export const isResetPasswordTokenValid = state =>
  state.users.resetPassword.isValid;
export const isResetPasswordTokenBeingValidated = state =>
  state.users.resetPassword.isValidatingToken;
export const getResetPasswordUser = state => state.users.resetPassword.user;
export const getResetPasswordTokenMessage = state =>
  state.users.resetPassword.message;

export const getIsPostingInvitedTeachers = state =>
  fromUsers.getIsPostingInvitedTeachers(state.users);
export const getInvitedTeachersPosting = state =>
  fromUsers.getInvitedTeachersPosting(state.users);
export const getInvitedTeachersResults = state =>
  fromUsers.getInvitedTeachersResults(state.users);
export const getTeachersInvitedCount = state =>
  fromUsers.getTeachersInvitedCount(state.users);
export const getInvitedEmailsByResultStatus = state =>
  fromUsers.getInvitedEmailsByResultStatus(state.users);

export const getClass = (state, id) => fromClasses.getClass(state.classes, id);
export const getIsPostingClass = state =>
  state.classes.createClassStatus.isPosting;
export const getIsFetchingClasses = state => state.classes.byId.isFetching;
export const getPostingClassMessage = state =>
  state.classes.createClassStatus.message;
export const getNewClassId = state =>
  state.classes.createClassStatus.newClassId;
export const getClassesForActiveUser = state => {
  const activeUser = getActiveUser(state);
  if (!activeUser) return [];
  const ids = state.classes.groupedByUserId[activeUser.id];
  if (!ids) return [];
  const classList = [];
  ids.map(id => classList.push(getClass(state, id)));
  return classList;
};

export const getAllSchools = state => fromSchools.getAllSchools(state.schools);
export const getSchool = (state, id) =>
  fromSchools.getSchool(state.schools, id);
export const getIsFetchingSchools = state =>
  fromSchools.getIsFetchingSchools(state.schools);
export const getIsFetchingLicenses = state =>
  fromSchools.getIsFetchingLicenses(state.schools);
export const getSchoolSeatLimit = (state, id, type) =>
  fromSchools.getSchoolSeatLimit(state.schools, id, type);
export const getLicensesForSchool = (state, id) =>
  fromSchools.getLicensesForSchool(state.schools, id);
export const getActiveSchoolSeatLimit = (state, type) => {
  const id = getActiveSchoolId(state);
  if (!id) return undefined;
  return getSchoolSeatLimit(state, id, type);
};
export const getActiveSchool = state => {
  const id = getActiveSchoolId(state);
  if (!id) return undefined;
  return getSchool(state, id);
};
export const getActiveSchoolLicense = state => {
  const id = getActiveSchoolId(state);
  if (!id) return undefined;
  return getLicensesForSchool(state, id);
};

export const getFormFields = (state, formId) =>
  fromFields.getFieldsByFormId(state.fields, formId);
export const getField = (state, fieldId) =>
  fromFields.getField(state.fields, fieldId);

export const getForm = (state, formId) =>
  fromForms.getForm(state.forms, formId);
export const getMultipageFormValues = (state, id) =>
  fromForms.getMultipageFormValues(state.forms, id);
export const getFocusedFormField = (state, formId) =>
  fromForms.getFocusedFormField(state.forms, formId);
export const isFormPosting = (state, formId) =>
  fromForms.isFormPosting(state.forms, formId);
export const isFormReconciling = (state, formId) =>
  fromForms.isFormReconciling(state.forms, formId);
export const isFormAwaitingUpdatedToken = (state, formId) =>
  fromForms.isFormAwaitingUpdatedToken(state.forms, formId);
export const getFormMessage = (state, formId) =>
  fromForms.getFormMessage(state.forms, formId);
export const isFormReadOnly = (state, formId) =>
  fromForms.isFormReadOnly(state.forms, formId);

export const isModalActive = (state, id) => fromUi.isModalActive(state.ui, id);
export const isConfirmationDialogActive = (state, name) =>
  fromUi.isConfirmationDialogActive(state.ui, name);
export const getConfirmationDialog = (state, name) =>
  fromUi.getConfirmationDialog(state.ui, name);
export const getPageError = (state, route) =>
  fromUi.getPageError(state.ui, route);
export const getMessage = (state, id) => fromUi.getMessage(state.ui, id);

export const getSchoolStatsBreakdown = state =>
  fromStats.getSchoolStatsBreakdown(state.stats);
export const getSchoolStatsById = (state, id) =>
  fromStats.getSchoolStatsById(state.stats, id);
export const isFetchingStats = (state, reducer) =>
  fromStats.isFetchingStats(state.stats, reducer);
export const getGenericStats = (state, endpoint) =>
  fromStats.getGenericStats(state.stats, endpoint);

export const getClassListById = (state, classId) =>
  fromLists.getClassListById(state.lists, classId);
export const getListById = (state, id) =>
  fromLists.getListById(state.lists, id);
export const getStatsListByType = (state, type) =>
  fromLists.getListById(state.lists, `${type}_stats`);
export const getNewIdsForList = (state, id) =>
  fromLists.getNewIdsForList(state.lists, id);

export const getMessageProps = (state, id) =>
  fromLocales.getMessageProps(state.locales, id);
export const messageIdExists = (state, id) =>
  fromLocales.messageIdExists(state.locales, id);
export const getObjectiveMessageIdsByModel = (state, id) =>
  fromLocales.getObjectiveMessageIdsByModel(state.locales, id);
export const getQuestionMessageIdsByModel = (state, id) =>
  fromLocales.getQuestionMessageIdsByModel(state.locales, id);
